import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from 'sonner';
import { companyInfo, servicesList, serviceAreas } from '../data/mock';
import { pageSEO } from '../data/seoData';
import SEOHead from '../components/SEOHead';
import { enrichFormData } from '../utils/attribution';

const ContactPage = () => {
  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', phone: '', service: '', message: '', city: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Enrich form data with attribution tracking
      const enrichedData = enrichFormData({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        service: formData.service,
        message: formData.message,
        city: formData.city,
        pageName: 'Contact Page',
        pageUri: window.location.href
      }, 'contact_form');

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(enrichedData)
      });

      const data = await response.json();

      if (data.status === 'success') {
        toast.success('Thank you! We\'ll contact you within 24 hours.');
        setFormData({ firstName: '', lastName: '', email: '', phone: '', service: '', message: '', city: '' });
        
        // Fire GTM event (no PII)
        if (window.dataLayer) {
          window.dataLayer.push({
            event: 'lead_form_submit',
            form_type: 'contact_form',
            service_selected: formData.service,
            lead_score: data.score,
            lead_priority: data.priority
          });
        }
      } else {
        throw new Error(data.message || 'Submission failed');
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <SEOHead 
        title={pageSEO.contact.title}
        description={pageSEO.contact.description}
        canonical="/contact"
      />
      {/* Hero */}
      <section className="relative py-24 bg-neutral-900">
        <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{ backgroundImage: `url('https://images.pexels.com/photos/4756489/pexels-photo-4756489.jpeg?auto=compress&cs=tinysrgb&w=1260')` }} />
        <div className="relative z-10 max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">Get Your Free Estimate</h1>
          <p className="text-xl text-neutral-300 max-w-3xl mx-auto">Ready to transform your home? Contact us today and our team will follow up promptly.</p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Form */}
            <div>
              <h2 className="text-2xl font-bold text-neutral-900 mb-2">Request a Free Estimate</h2>
              <p className="text-neutral-600 mb-8">Fill out the form below and we'll get back to you within 24 hours.</p>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="firstName" className="text-neutral-700 mb-2 block">First Name *</Label>
                    <Input id="firstName" name="firstName" value={formData.firstName} onChange={handleInputChange} required placeholder="John" className="h-12" />
                  </div>
                  <div>
                    <Label htmlFor="lastName" className="text-neutral-700 mb-2 block">Last Name *</Label>
                    <Input id="lastName" name="lastName" value={formData.lastName} onChange={handleInputChange} required placeholder="Smith" className="h-12" />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email" className="text-neutral-700 mb-2 block">Email Address *</Label>
                    <Input id="email" name="email" type="email" value={formData.email} onChange={handleInputChange} required placeholder="john@example.com" className="h-12" />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-neutral-700 mb-2 block">Phone Number *</Label>
                    <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleInputChange} required placeholder="(214) 555-0123" className="h-12" />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="service" className="text-neutral-700 mb-2 block">Service Needed *</Label>
                    <Select value={formData.service} onValueChange={(value) => handleSelectChange('service', value)} required>
                      <SelectTrigger className="h-12" data-testid="service-select"><SelectValue placeholder="Select a service" /></SelectTrigger>
                      <SelectContent>
                        {servicesList.map((service) => (
                          <SelectItem key={service.id} value={service.id}>{service.shortTitle}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="city" className="text-neutral-700 mb-2 block">Your City</Label>
                    <Select value={formData.city} onValueChange={(value) => handleSelectChange('city', value)}>
                      <SelectTrigger className="h-12" data-testid="city-select"><SelectValue placeholder="Select your city" /></SelectTrigger>
                      <SelectContent>
                        {serviceAreas.map((area) => (
                          <SelectItem key={area} value={area}>{area}, TX</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="message" className="text-neutral-700 mb-2 block">Project Details</Label>
                  <Textarea id="message" name="message" value={formData.message} onChange={handleInputChange} placeholder="Tell us about your project..." rows={5} className="resize-none" />
                </div>

                <Button type="submit" size="lg" className="w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold py-6" disabled={isSubmitting}>
                  {isSubmitting ? 'Submitting...' : <>Submit Request <Send className="ml-2 w-5 h-5" /></>}
                </Button>
              </form>
              <p className="text-sm text-neutral-500 mt-4 text-center">By submitting, you agree to receive communications from Gray Contracting.</p>
            </div>

            {/* Contact Info */}
            <div>
              <div className="bg-neutral-900 text-white rounded-2xl p-8 mb-8">
                <h3 className="text-xl font-bold mb-6">Contact Information</h3>
                <div className="space-y-6">
                  <a href={`tel:${companyInfo.phone}`} className="flex items-start gap-4 hover:text-amber-400 transition-colors">
                    <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center flex-shrink-0"><Phone className="w-6 h-6 text-white" /></div>
                    <div><div className="text-sm text-neutral-400">Phone</div><div className="text-lg font-semibold">{companyInfo.phone}</div></div>
                  </a>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center flex-shrink-0"><MapPin className="w-6 h-6 text-white" /></div>
                    <div><div className="text-sm text-neutral-400">Address</div><div className="font-semibold">{companyInfo.addressStreet}</div><div className="text-neutral-300">{companyInfo.addressCity}, {companyInfo.addressState} {companyInfo.addressZip}</div></div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center flex-shrink-0"><Clock className="w-6 h-6 text-white" /></div>
                    <div><div className="text-sm text-neutral-400">Business Hours</div><div className="font-semibold">Mon - Fri: 7:00 AM - 6:00 PM</div><div className="text-neutral-300">Sat: 8:00 AM - 2:00 PM</div></div>
                  </div>
                </div>
              </div>

              <div className="bg-neutral-50 rounded-2xl p-8">
                <h3 className="text-xl font-bold text-neutral-900 mb-6">Why Choose Gray Contracting?</h3>
                <div className="space-y-4">
                  {["55+ years of trusted experience", "Family-owned and operated", "Free, no-obligation estimates", "Fully licensed and insured", "Lead and mold certified", "Transparent pricing", "Quality craftsmanship guaranteed"].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                      <span className="text-neutral-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-16 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-neutral-900 mb-2">Serving the Dallas-Fort Worth Metroplex</h2>
            <p className="text-neutral-600">We proudly provide services throughout these communities</p>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {serviceAreas.map((area, i) => (
              <span key={i} className="px-4 py-2 bg-white rounded-full text-sm font-medium text-neutral-700 shadow-sm">{area}, TX</span>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Services */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-neutral-900 text-center mb-8">Request a Quote for Any Service</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {servicesList.map((service) => (
              <Link key={service.id} to={`/services/${service.id}`} className="bg-neutral-50 hover:bg-amber-50 p-4 rounded-lg text-center transition-colors group">
                <span className="font-medium text-neutral-700 group-hover:text-amber-600">{service.shortTitle}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
